import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAPI } from "../contexts/APIContext";
import logger from "../utils/logger";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const PixleyStarsModal = ({ isOpen, onClose }) => {
  const { user, subscriptionStatus } = useAuth();
  const { apiClient } = useAPI();
  const navigate = useNavigate();
  const [starDeliveryAmount, setStarDeliveryAmount] = useState(50);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  useEffect(() => {
    const fetchStarDeliveryAmount = async () => {
      if (!isOpen || !apiClient || !user?.id) return;
      try {
        const response = await apiClient.getCosts(
          user.id,
          subscriptionStatus?.subscriptionType || "NONE",
        );
        if (response.status && response.data?.rate_limits?.star_delivery) {
          setStarDeliveryAmount(response.data.rate_limits.star_delivery);
        }
      } catch (error) {
        logger.error("Error loading star delivery amount:", error);
      }
    };
    if (isOpen) fetchStarDeliveryAmount();
  }, [isOpen, apiClient, user?.id, subscriptionStatus?.subscriptionType]);

  const getNextStarDelivery = () => {
    const today = new Date();
    const nextDelivery = new Date(today);
    nextDelivery.setDate(today.getDate() + 7);
    const daysUntil = Math.ceil((nextDelivery - today) / (1000 * 60 * 60 * 24));
    return { days: daysUntil, stars: starDeliveryAmount };
  };

  const nextDelivery = getNextStarDelivery();
  const currentStars = user?.pixley_stars ?? 0;

  const starPackages = [
    { stars: 75, price: 4.99, label: "Starter Pack", desc: "A few short stories" },
    { stars: 175, price: 9.99, label: "Story Pack", desc: "Best for hobbyists", popular: true },
    { stars: 400, price: 19.99, label: "Creator Pack", desc: "For power users" },
    { stars: 1000, price: 49.99, label: "Ultimate Pack", desc: "Creative freedom" },
  ];

  const handlePurchase = async (packageItem) => {
    if (isProcessing) return;
    try {
      setIsProcessing(true);
      onClose();
      navigate('/settings?tab=shop');
    } catch (error) {
      logger.error('Error purchasing stars:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const modalContent = (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pointer-events-none">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0"
            onClick={handleBackdropClick}
            style={{ background: 'rgba(11, 26, 51, 0.42)', backdropFilter: 'blur(8px)', pointerEvents: 'auto' }}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="relative z-10 w-full max-w-lg max-h-[92vh] overflow-y-auto"
            style={{
              background: 'linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)',
              borderRadius: 28,
              boxShadow: '0px 5px 0px rgba(11,26,51,0.10), 0px 24px 64px rgba(11,26,51,0.20)',
              border: '2px solid rgba(11,26,51,0.06)',
              pointerEvents: 'auto',
              fontFamily: "'Inter', 'Fredoka', sans-serif",
            }}
          >
            {/* Decorative shapes */}
            <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', borderRadius: 28 }}>
              <div style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, borderRadius: '50%', background: 'rgba(0,101,248,0.05)' }} />
              <div style={{ position: 'absolute', bottom: -80, left: -80, width: 260, height: 260, borderRadius: '50%', background: 'rgba(0,202,255,0.06)' }} />
            </div>

            {/* Close */}
            <button
              onClick={onClose}
              style={{
                position: 'absolute', top: 16, right: 16, zIndex: 20,
                width: 40, height: 40, borderRadius: 12,
                background: '#FFFFFF', border: '2px solid rgba(11,26,51,0.08)', cursor: 'pointer',
                boxShadow: '0px 3px 0px rgba(11,26,51,0.06)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, color: '#334155',
              }}
            >
              ✕
            </button>

            <div className="relative p-6 sm:p-8">
              {/* Hero Balance */}
              <section className="mb-5 text-center" style={{ paddingTop: 8 }}>
                <img src="/assets/pixley_stars.png" alt="Stars" className="w-12 h-12 mx-auto mb-3" style={{ filter: 'drop-shadow(0px 4px 6px rgba(0,101,248,0.18))' }} />
                <h1 className="text-2xl sm:text-3xl mb-1" style={{ ...FREDOKA, color: '#0B1A33', fontWeight: 700, letterSpacing: '-0.01em' }}>
                  Buy More Stars
                </h1>
                <p className="text-sm mb-4" style={{ ...FREDOKA, color: '#5A6B85', fontWeight: 500 }}>
                  Power up your storytelling
                </p>
                <div
                  className="inline-flex flex-col items-center px-6 py-2.5"
                  style={{
                    background: '#FFFFFF',
                    border: '2px solid rgba(0,101,248,0.12)',
                    borderRadius: 999,
                    boxShadow: '0px 3px 0px rgba(11,26,51,0.06)',
                  }}
                >
                  <span className="text-[10px] uppercase font-bold" style={{ ...FREDOKA, color: '#5A6B85', letterSpacing: '0.12em' }}>
                    Current Balance
                  </span>
                  <span className="text-xl flex items-center gap-1.5" style={{ ...FREDOKA, color: '#0065F8', fontWeight: 700 }}>
                    {currentStars.toLocaleString()}
                    <img src="/assets/pixley_stars.png" alt="" className="w-4 h-4" />
                  </span>
                </div>
              </section>

              {/* Next Delivery */}
              <section
                className="mb-5 p-3.5 flex items-center gap-3"
                style={{
                  background: '#FFFFFF',
                  border: '2px solid rgba(11,26,51,0.06)',
                  borderRadius: 16,
                  boxShadow: '0px 2px 0px rgba(11,26,51,0.04)',
                }}
              >
                <div
                  className="w-10 h-10 shrink-0 flex items-center justify-center"
                  style={{ background: 'rgba(0,101,248,0.10)', borderRadius: 12 }}
                >
                  <svg className="w-5 h-5" fill="none" stroke="#0065F8" strokeWidth="2.2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm" style={{ ...FREDOKA, color: '#0B1A33', fontWeight: 700 }}>Next Star Delivery</h3>
                  <p className="text-xs" style={{ ...FREDOKA, color: '#5A6B85' }}>
                    {nextDelivery.stars} stars arriving in {nextDelivery.days} days
                  </p>
                </div>
              </section>

              {/* Packages Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                {starPackages.map((pkg, idx) => (
                  <button
                    key={idx}
                    onClick={() => handlePurchase(pkg)}
                    disabled={isProcessing}
                    className={`group relative p-4 transition-all duration-200 active:scale-[0.98] text-left ${
                      isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:-translate-y-0.5'
                    }`}
                    style={{
                      background: pkg.popular ? '#0065F8' : '#FFFFFF',
                      border: pkg.popular ? '2px solid #0065F8' : '2px solid rgba(11,26,51,0.08)',
                      borderRadius: 18,
                      boxShadow: pkg.popular
                        ? '0px 4px 0px #073B8E, 0px 12px 28px rgba(0,101,248,0.18)'
                        : '0px 2px 0px rgba(11,26,51,0.04), 0px 4px 12px rgba(11,26,51,0.04)',
                    }}
                  >
                    {pkg.popular && (
                      <div
                        className="absolute -top-2.5 left-1/2 px-2.5 py-1"
                        style={{
                          transform: 'translateX(-50%)',
                          background: '#FF6B9A',
                          color: '#FFFFFF',
                          borderRadius: 999,
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: '0.08em',
                          textTransform: 'uppercase',
                          boxShadow: '0px 2px 0px #C94D73',
                          whiteSpace: 'nowrap',
                          ...FREDOKA,
                        }}
                      >
                        Most Popular
                      </div>
                    )}
                    <div className="flex justify-between items-start mb-3">
                      <img src="/assets/pixley_stars.png" alt="" className="w-6 h-6" style={{ filter: pkg.popular ? 'brightness(0) invert(1)' : 'none' }} />
                      <span className="text-lg" style={{ ...FREDOKA, color: pkg.popular ? '#FFFFFF' : '#0B1A33', fontWeight: 700 }}>
                        ${pkg.price}
                      </span>
                    </div>
                    <h4 className="text-base mb-0.5" style={{ ...FREDOKA, color: pkg.popular ? '#FFFFFF' : '#0B1A33', fontWeight: 700 }}>
                      {pkg.stars} Stars
                    </h4>
                    <p className="text-xs leading-snug" style={{ ...FREDOKA, color: pkg.popular ? 'rgba(255,255,255,0.85)' : '#5A6B85' }}>
                      {pkg.desc}
                    </p>
                  </button>
                ))}
              </div>

              {/* View Shop */}
              <button
                onClick={() => { onClose(); navigate("/settings?tab=shop"); }}
                className="w-full py-3 transition-all duration-150 active:scale-[0.98]"
                style={{
                  ...FREDOKA,
                  background: '#FFFFFF',
                  color: '#0065F8',
                  fontWeight: 700,
                  fontSize: 14,
                  border: '2px solid rgba(0,101,248,0.18)',
                  borderRadius: 999,
                  boxShadow: '0px 3px 0px rgba(11,26,51,0.06)',
                }}
              >
                View Entire Shop
              </button>

              <p className="mt-4 text-center text-xs italic" style={{ ...FREDOKA, color: '#8CA0BC' }}>
                One-time purchase · No subscription needed
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );

  if (typeof window !== "undefined") {
    return createPortal(modalContent, document.body);
  }
  return null;
};

export default PixleyStarsModal;
